import React, { useState, useEffect } from 'react';
import { services } from './mock';
import { Heart, Zap, Award, Users } from 'lucide-react';

const Home = () => {
  const stats = [
    { label: 'Happy Customers', value: '500+', icon: Users },
    { label: 'Tests Completed', value: '1000+', icon: Award },
    { label: 'Success Rate', value: '99.8%', icon: Zap },
    { label: 'Years Experience', value: '15+', icon: Heart },
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-teal-50 via-white to-cyan-50 overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="text-gray-900">Authentic Testing,</span>
            <br />
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Trusted Results</span>
          </h1>
          <p className="text-lg md:text-xl mb-8 text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Professional laboratory services for Pashmina authentication and shawl adulteration testing. Ensuring quality and authenticity with scientific precision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/book"
              className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-lg transition transform hover:scale-105"
            >
              Book Appointment →
            </a>
            <a
              href="#services"
              className="border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-50 transition"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Testing Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive laboratory testing designed to meet the highest standards of authenticity verification
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition duration-300 border border-gray-100"
              >
                <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">{service.name}</h3>
                  <p className="text-teal-100 font-medium">{service.duration}</p>
                </div>
                <div className="p-8">
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 mb-3">Key Features:</h4>
                    <ul className="space-y-2">
                      {service.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <span className="w-2 h-2 bg-teal-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-2xl font-bold text-teal-600">₹{service.price}</span>
                    <a
                      href="/book"
                      className="bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-700 transition"
                    >
                      Book Now
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Endemik Solutions?</h2>
            <p className="text-xl text-gray-600">Leading the way in textile authentication and quality assurance</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-lg border border-gray-100 hover:shadow-lg transition text-center">
                  <Icon className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Verify Your Shawls?</h2>
          <p className="text-xl mb-8 text-teal-100">
            No login required. Book your appointment in minutes and receive expert verification results.
          </p>
          <a
            href="/book"
            className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition transform hover:scale-105"
          >
            Book Appointment Now
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
